import { Machine, MachineConfig, Interpreter, DoneInvokeEvent, assign, State } from 'xstate';
import { Lens } from 'monocle-ts';

import Async from '../../Async';
import Data from '../../Data';

import { LooksSearchResponse } from '../../Async/Looks/response_models';

// States
export enum States {
  LOAD_INITIAL_LOOKS_REQUEST = 'LOAD_INITIAL_LOOKS_REQUEST',

  SEARCH_LOOKS_SCREEN = 'SEARCH_LOOKS_SCREEN',
  SEARCH_LOOKS_REQUEST = 'SEARCH_LOOKS_REQUEST',
  SEARCH_LOOKS_ERROR = 'SEARCH_LOOKS_ERROR',
}

export type AutomataStates = {
  states: {
    [States.LOAD_INITIAL_LOOKS_REQUEST]: {};

    [States.SEARCH_LOOKS_SCREEN]: {};
    [States.SEARCH_LOOKS_REQUEST]: {};
    [States.SEARCH_LOOKS_ERROR]: {};
  };
};

// Events
export enum Events {
  ENTER_DATA = 'ENTER_DATA',
  KEY_PRESSED = 'KEY_PRESSED',
  DATA_SUBMITTED = 'DATA_SUBMITTED',
}

export type EnterData = {
  type: Events.ENTER_DATA;
  term: string;
};
export type KeyPressed = {
  type: Events.KEY_PRESSED;
  key: string;
};
export type DataSubmitted = {
  type: Events.DATA_SUBMITTED;
};
export type AutomataEvent = EnterData | KeyPressed | DataSubmitted;

// Context

export type AutomataContext = {
  term: string;
};

export type AutomataService = Interpreter<AutomataContext, AutomataStates, AutomataEvent>;
export type CurrentState = State<AutomataContext, AutomataEvent>;
export type Machine = MachineConfig<AutomataContext, AutomataStates, AutomataEvent>;
export type Send = AutomataService['send'];

// Services

export const loadInitialLooks = (c: AutomataContext, e: AutomataEvent) => Async.looks.list(10, 0);

export const searchLooks = (c: AutomataContext, e: AutomataEvent) => Async.looks.search(c.term);

// Actions
export const sendLooksToStore = (c: AutomataContext, e: DoneInvokeEvent<LooksSearchResponse>) =>
  Data.store.dispatch(Data.creators.looks.saveLooksToStore(e.data.record_list));

export const assignSearchTermToContext = assign(
  (c: AutomataContext, e: EnterData): AutomataContext => Lens.fromProp<AutomataContext>()('term').set(e.term)(c),
);

// Guards
export const enterKeyPressed = (c: AutomataContext, e: KeyPressed): boolean => e.key === 'Enter';

// Machine
export const config: MachineConfig<AutomataContext, AutomataStates, AutomataEvent> = {
  id: 'Looker:Looks:Search:Machine',
  initial: States.LOAD_INITIAL_LOOKS_REQUEST,
  context: {
    term: '',
  },
  states: {
    [States.LOAD_INITIAL_LOOKS_REQUEST]: {
      invoke: {
        src: 'loadInitialLooks',
        onDone: {
          target: States.SEARCH_LOOKS_SCREEN,
          actions: ['sendLooksToStore'],
        },
        onError: {
          target: States.SEARCH_LOOKS_SCREEN,
          actions: [(c, e) => console.log({ c, e })],
        },
      },
    },
    [States.SEARCH_LOOKS_SCREEN]: {
      on: {
        [Events.ENTER_DATA]: {
          actions: ['assignSearchTermToContext'],
        },
        [Events.KEY_PRESSED]: [
          {
            target: States.SEARCH_LOOKS_REQUEST,
            cond: 'enterKeyPressed',
          },
        ],
        [Events.DATA_SUBMITTED]: {
          target: States.SEARCH_LOOKS_REQUEST,
        },
      },
    },
    [States.SEARCH_LOOKS_REQUEST]: {
      invoke: {
        src: 'searchLooks',
        onDone: {
          target: States.SEARCH_LOOKS_SCREEN,
          actions: ['sendLooksToStore'],
        },
        onError: {
          target: States.SEARCH_LOOKS_ERROR,
        },
      },
    },
    [States.SEARCH_LOOKS_ERROR]: {
      on: {
        [Events.ENTER_DATA]: {
          actions: ['assignSearchTermToContext'],
        },
        [Events.KEY_PRESSED]: [
          {
            target: States.SEARCH_LOOKS_REQUEST,
            cond: 'enterKeyPressed',
          },
        ],
        [Events.DATA_SUBMITTED]: {
          target: States.SEARCH_LOOKS_REQUEST,
        },
      },
    },
  },
};

const options: any = {
  services: {
    searchLooks,
    loadInitialLooks,
  },
  actions: {
    sendLooksToStore,
    assignSearchTermToContext,
  },
  guards: {
    enterKeyPressed,
  },
};

export default Machine<AutomataContext, AutomataStates, AutomataEvent>(config, options);
